import { $ } from "../lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BG2, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { DATE_RANGES, MOCK_TODAY, filterUntaggedByDate } from "../lib/dates";
import { EXPENSE_CATEGORIES } from "../lib/constants";
import { inRangeYMD, rangeBounds } from "../dateRange";
import { useMemo, useRef, useState } from "react";
import { JobPicker } from "../components/JobPicker";
import { expenseRange, toPickerJobs } from "../jobPicker";

// ─── INBOX ADD EXPENSE FORM (inline in Expense Management) ──────────────────

function InboxAddExpenseForm({ pickerJobs, onSubmit, onCancel }) {
  const [desc, setDesc]         = useState("");
  const [vendor, setVendor]     = useState("");
  const [amount, setAmount]     = useState("");
  const [category, setCategory] = useState("materials");
  const [expDate, setExpDate]   = useState(() => new Date().toISOString().split('T')[0]);
  const [jobId, setJobId]       = useState("");
  const [saving, setSaving]     = useState(false); // one save at a time, so a double click can't add it twice

  const inputStyle = { padding:"7px 10px", borderRadius:5, border:`1px solid ${BORDER}`, background:CARD, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DARK, outline:"none", boxSizing:"border-box" };

  async function handleSubmit() {
    if (saving || !jobId || !desc.trim() || !amount || parseFloat(amount) <= 0) return;
    setSaving(true);
    try { await onSubmit({
      jobId,
      description: desc.trim(),
      vendor: vendor.trim() || null,
      amount: parseFloat(amount),
      category,
      expenseDate: expDate || null,
      source: "manual",
    }); } finally { setSaving(false); }
  }

  return (
    <div style={{ padding:"16px 18px", borderRadius:6, border:`1px solid ${BORDER}`, background:CARD }}>
      <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, fontWeight:600, color:MID, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" }}>Add manual expense</div>
      <div className="r-cols-3" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Description *</label>
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Cash supplies" style={{ ...inputStyle, width:"100%" }} />
        </div>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Vendor / Payee</label>
          <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g. Home Depot" style={{ ...inputStyle, width:"100%" }} />
        </div>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Amount *</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="420.00" style={{ ...inputStyle, width:"100%" }} />
        </div>
      </div>
      <div className="r-cols-3" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:12 }}>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, width:"100%" }}>
            {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Date</label>
          <input type="date" value={expDate} onChange={e => setExpDate(e.target.value)} style={{ ...inputStyle, width:"100%" }} />
        </div>
        <div>
          <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Assign to a job *</label>
          {pickerJobs.length === 0 ? (
            <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:MID, lineHeight:1.5 }}>You have no jobs yet. Add or sync a job first, then you can add a cash cost to it.</div>
          ) : (
            <JobPicker jobs={pickerJobs} range={expenseRange(expDate)} value={jobId} onChange={setJobId}
              placeholder="Choose a job" ariaLabel="Assign to a job" />
          )}
        </div>
      </div>
      <div className="r-btnrow" style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
        <button className="btn" onClick={onCancel} style={{ fontSize:11 }}>Cancel</button>
        <button className="btn act" onClick={handleSubmit} disabled={saving || !jobId || !desc.trim() || !amount || parseFloat(amount) <= 0} style={{ fontSize:11, padding:"6px 16px", opacity: (saving || !jobId || !desc.trim() || !amount || parseFloat(amount) <= 0) ? 0.4 : 1 }}>{saving ? "Saving..." : "Save Expense"}</button>
      </div>
    </div>
  );
}

function SyncReview({ autoMatched, suggested, untagged, allTagged, overhead, dismissed, jobSummaries, vendorRules, onConfirmSuggestion, onTag, onMarkOverhead, onDismiss, onRestore, onRetag, onUndoAutoMatch, onSaveVendorRule, onAddExpense, dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd }) {
  const [section, setSection]             = useState("review");
  const [selections, setSelections]       = useState({});    // itemId -> jobId
  const [expandAuto, setExpandAuto]       = useState(false);
  const [retagItem, setRetagItem]         = useState(null);  // item being re-tagged
  const [retagJobId, setRetagJobId]       = useState("");
  const [searchTagged, setSearchTagged]   = useState("");
  const [searchReview, setSearchReview]   = useState("");
  const [searchQb, setSearchQb]           = useState("");
  const [toast, setToast]                 = useState(null);   // { message, color }
  const [confirmUndo, setConfirmUndo]     = useState(null);   // item awaiting undo confirmation
  const [bulkJobId, setBulkJobId]         = useState("");     // vendor-level bulk assign job
  const [showAddExpense, setShowAddExpense] = useState(false); // inline add-expense form
  const toastTimer = useRef(null);

  function showToast(message, color) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, color: color || ACCENT2 });
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }

  // Job options for dropdowns
  const liveJobOptions = (jobSummaries || []).map(j => ({
    value: j.id, label: j.name, client: j.clientName || "",
  }));
  const jobOptions = liveJobOptions; // never fall back to sample jobs on a real account
  const pickerJobs = useMemo(() => toPickerJobs(jobSummaries), [jobSummaries]);

  // Job lookup for displaying names
  const jobLookup = useMemo(() => {
    const m = {};
    jobOptions.forEach(j => { m[j.value] = j; });
    return m;
  }, [jobOptions]);

  // Filter items by date range
  const filteredAutoMatched = filterUntaggedByDate(autoMatched || [], dateRange, customStart, customEnd);
  const filteredOverhead    = filterUntaggedByDate(overhead || [], dateRange, customStart, customEnd);

  // Suggested + untagged: date filter first, then search filter
  const dateSuggested = filterUntaggedByDate(suggested || [], dateRange, customStart, customEnd);
  const dateUntagged  = filterUntaggedByDate(untagged || [], dateRange, customStart, customEnd);
  const filteredSuggested = searchReview
    ? dateSuggested.filter(i => { const q = searchReview.toLowerCase(); return (i.vendor||'').toLowerCase().includes(q) || (i.description||'').toLowerCase().includes(q); })
    : dateSuggested;
  const filteredUntagged = searchReview
    ? dateUntagged.filter(i => { const q = searchReview.toLowerCase(); return (i.vendor||'').toLowerCase().includes(q) || (i.description||'').toLowerCase().includes(q); })
    : dateUntagged;

  // Tagged: search filter
  const filteredTagged = (allTagged || []).filter(t => {
    if (!searchTagged) return true;
    const q = searchTagged.toLowerCase();
    return (t.vendor || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
  });

  // QB-direct expenses: tagged in QuickBooks before sync — not from inbox workflow
  const qbDirectAll = useMemo(() => {
    const result = [];
    (jobSummaries || []).forEach(job => {
      (job.purchases || []).forEach(p => {
        if (!String(p.Id).includes('_automatch_')) {
          result.push({ ...p, jobName: job.name, jobId: job.id });
        }
      });
    });
    return result.sort((a,b) => new Date(b.TxnDate) - new Date(a.TxnDate));
  }, [jobSummaries]); // eslint-disable-line react-hooks/exhaustive-deps

  const qbDirect = useMemo(() => {
    let items = qbDirectAll;
    if (dateRange !== "all") {
      const b = rangeBounds(dateRange, customStart, customEnd, MOCK_TODAY);
      if (b) items = items.filter(p => inRangeYMD(p.TxnDate, b.start, b.end));
    }
    if (searchQb) {
      const q = searchQb.toLowerCase();
      items = items.filter(p =>
        (p.EntityRef?.name||'').toLowerCase().includes(q) ||
        (p.Line?.[0]?.Description||'').toLowerCase().includes(q) ||
        (p.jobName||'').toLowerCase().includes(q)
      );
    }
    return items;
  }, [qbDirectAll, dateRange, customStart, customEnd, searchQb]); // eslint-disable-line react-hooks/exhaustive-deps

  const qbDirectTotal = qbDirect.reduce((s,p) => s + (p.TotalAmt||0), 0);

  // Group auto-matched by vendor → job for the summary
  const autoGrouped = useMemo(() => {
    const groups = {};
    filteredAutoMatched.forEach(item => {
      const job = jobLookup[item.taggedJobId];
      const key = `${item.vendor}→${item.taggedJobId}`;
      if (!groups[key]) groups[key] = { vendor: item.vendor, jobName: job?.label || 'Unknown Job', jobId: item.taggedJobId, items: [], total: 0 };
      groups[key].items.push(item);
      groups[key].total += item.amount;
    });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [filteredAutoMatched, jobLookup]);

  // Group needs-review items by vendor for bulk actions
  const reviewVendorGroups = useMemo(() => {
    const groups = {};
    [...filteredSuggested, ...filteredUntagged].forEach(item => {
      if (!groups[item.vendor]) groups[item.vendor] = { items: [], total: 0, hasSuggested: false };
      groups[item.vendor].items.push(item);
      groups[item.vendor].total += item.amount;
      if (item.suggestedJob) groups[item.vendor].hasSuggested = true;
    });
    return Object.entries(groups)
      .filter(([, g]) => g.items.length >= 2) // only group vendors with 2+ items
      .sort((a, b) => b[1].total - a[1].total);
  }, [filteredSuggested, filteredUntagged]);

  const needsReviewCount = dateSuggested.length + dateUntagged.length; // unfiltered by search for badge
  const autoTotal   = filteredAutoMatched.reduce((s, i) => s + i.amount, 0);
  const reviewTotal = [...filteredSuggested, ...filteredUntagged].reduce((s, i) => s + i.amount, 0);

  // ── Bulk actions ──

  function handleConfirmAllSuggestions() {
    const items = filteredSuggested.filter(i => i.suggestedJob);
    if (!items.length) return;
    items.forEach(item => {
      const job = jobLookup[item.suggestedJob];
      onConfirmSuggestion(item, item.suggestedJob, job?.label || '');
    });
    showToast(`${items.length} expenses confirmed ✓`, ACCENT2);
  }

  function handleBulkOverhead(items) {
    items.forEach(item => onMarkOverhead(item));
    showToast(`${items.length} marked as fixed costs`, AMBER);
  }

  function handleBulkDismiss(items) {
    items.forEach(item => onDismiss(item.id));
    showToast(`${items.length} dismissed`, DIM);
  }

  function handleBulkAssign(items, jobId) {
    const job = jobOptions.find(j => j.value === jobId);
    items.forEach(item => {
      if (item.suggestedJob) {
        onConfirmSuggestion(item, jobId, job?.label || '');
      } else {
        onTag(item, jobId, job?.label || '');
      }
    });
    setSearchReview("");
    showToast(`${items.length} tagged to ${job?.label || 'job'} ✓`, ACCENT2);
  }

  // Confirm a suggestion — accept the match
  function handleConfirmSuggestion(item) {
    const job = jobLookup[item.suggestedJob];
    onConfirmSuggestion(item, item.suggestedJob, job?.label || '');
    showToast(`Tagged to ${job?.label || 'job'} ✓`, ACCENT2);
  }

  // Assign a needs-attention item manually
  function handleAssign(item) {
    const jobId = selections[item.id];
    if (!jobId) return;
    const job = jobOptions.find(j => j.value === jobId);
    onTag(item, jobId, job?.label || '');
    setSelections(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    showToast(`Tagged to ${job?.label || 'job'} ✓`, ACCENT2);
  }

  // Change a suggested item to a different job
  function handleChangeSuggestion(item) {
    const jobId = selections[item.id];
    if (!jobId) return;
    const job = jobOptions.find(j => j.value === jobId);
    onConfirmSuggestion(item, jobId, job?.label || '');
    setSelections(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    showToast(`Tagged to ${job?.label || 'job'} ✓`, ACCENT2);
  }

  // Submit a re-tag
  function handleRetagSubmit() {
    if (!retagItem || !retagJobId) return;
    const job = jobOptions.find(j => j.value === retagJobId);
    onRetag(retagItem, retagJobId, job?.label || '');
    showToast(`Moved to ${job?.label || 'job'} ✓`, ACCENT2);
    setRetagItem(null);
    setRetagJobId("");
  }

  // Confirm-then-undo auto-match (two-step to prevent accidents)
  function handleUndoConfirmed() {
    if (!confirmUndo) return;
    onUndoAutoMatch(confirmUndo);
    showToast(`Removed auto-match — moved to Needs Review`, AMBER);
    setConfirmUndo(null);
  }

  // Section tab data
  const sections = [
    { key: "auto",     label: "Auto-Matched",  count: filteredAutoMatched.length, color: ACCENT2 },
    { key: "review",   label: "Needs Review",   count: needsReviewCount,           color: needsReviewCount > 0 ? AMBER : ACCENT2 },
    { key: "fixed",    label: "Fixed Costs",    count: filteredOverhead.length,    color: ACCENT2 },
    { key: "tagged",   label: "All Tagged",      count: filteredTagged.length,      color: ACCENT2 },
    { key: "qbtagged", label: "Tagged in QuickBooks",   count: qbDirect.length,            color: ACCENT2 },
  ];

  return (
    <div className="r-page" style={{ padding:"32px 36px",background:BG,minHeight:"100vh",position:"relative" }}>

      {/* Toast notification */}
      {toast && (
        <div className="r-toast" style={{ position:"fixed",top:24,right:24,zIndex:800,padding:"12px 20px",borderRadius:6,background:CARD,border:`1px solid ${toast.color}44`,boxShadow:"0 8px 24px rgba(44,36,22,0.15)",display:"flex",alignItems:"center",gap:8,animation:"fadeIn 0.2s ease" }}>
          <div style={{ width:8,height:8,borderRadius:"50%",background:toast.color,flexShrink:0 }}/>
          <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DARK,fontWeight:500 }}>{toast.message}</span>
        </div>
      )}

      {/* Undo auto-match confirmation dialog */}
      {confirmUndo && (
        <div style={{ position:"fixed",inset:0,background:"rgba(44,36,22,0.4)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:24 }}>
          <div className="r-gate" style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:8,padding:"28px 32px",maxWidth:440,boxShadow:"0 20px 60px rgba(44,36,22,0.2)" }}>
            <h3 style={{ fontFamily:"'Lora',serif",fontSize:17,fontWeight:500,color:DARK,marginBottom:8 }}>Undo auto-match?</h3>
            <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.6,marginBottom:6 }}>
              This will remove <strong style={{ color:DARK }}>{confirmUndo.vendor}</strong> · {$(confirmUndo.amount)} from its matched job and move it to Needs Review for manual assignment.
            </p>
            <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,marginBottom:20 }}>The expense will be removed from job costs until you re-assign it.</p>
            <div className="r-btnrow" style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
              <button className="btn" onClick={()=>setConfirmUndo(null)}>Cancel</button>
              <button className="btn" onClick={handleUndoConfirmed} style={{ borderColor:"rgba(140,107,48,0.3)",color:AMBER }}>Yes, undo match</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="r-head" style={{ marginBottom:20, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div>
            <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>Expense Management</h1>
            <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,marginTop:4 }}>Review how Canopy matched your expenses to jobs. Confirm, correct, or assign what needs attention.</p>
          </div>
        </div>
        {/* Date range toggle */}
        <div className="r-datebtns" style={{ display:"flex",alignItems:"center",gap:6,flexWrap:"wrap" }}>
          <div className="r-seg" style={{ display:"flex",border:`1px solid ${BORDER}`,borderRadius:5,overflow:"hidden" }}>
            {DATE_RANGES.map((r,i) => (
              <button key={r.key} onClick={()=>setDateRange(r.key)} style={{ cursor:"pointer",padding:"7px 13px",fontSize:11,fontWeight:500,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.03em",border:"none",borderRight:i<DATE_RANGES.length-1?`1px solid ${BORDER}`:"none",background:dateRange===r.key?ACCENT:CARD,color:dateRange===r.key?CARD:MID,transition:"all 0.15s" }}>{r.label}</button>
            ))}
          </div>
          <button onClick={()=>setDateRange("custom")} style={{ cursor:"pointer",padding:"7px 14px",fontSize:11,fontWeight:500,fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.03em",border:`1px solid ${BORDER}`,borderRadius:5,background:dateRange==="custom"?ACCENT:CARD,color:dateRange==="custom"?CARD:MID,transition:"all 0.15s" }}>Custom</button>
          {dateRange === "custom" && (
            <div className="r-dates" style={{ display:"flex",alignItems:"center",gap:6,marginLeft:4 }}>
              <input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} style={{ padding:"5px 10px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DARK,outline:"none",cursor:"pointer" }} />
              <span style={{ fontSize:11,color:DIM,fontFamily:"'DM Sans',sans-serif" }}>→</span>
              <input type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} style={{ padding:"5px 10px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DARK,outline:"none",cursor:"pointer" }} />
            </div>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="r-cols-tiles" style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28 }}>
        {[
          { label:"Auto-Matched",     val:filteredAutoMatched.length, sub:$(autoTotal)+" matched automatically",               color:filteredAutoMatched.length>0?ACCENT2:DIM },
          { label:"Needs Review",      val:needsReviewCount,          sub:$(reviewTotal)+" awaiting your input",                color:needsReviewCount>0?AMBER:ACCENT2 },
          { label:"Fixed Costs",       val:filteredOverhead.length,    sub:$(filteredOverhead.reduce((s,o)=>s+o.amount,0))+" overhead", color:filteredOverhead.length>0?ACCENT2:DIM },
          { label:"All Tagged",        val:filteredTagged.length,      sub:"confirmed + auto-matched + manual",                 color:ACCENT2 },
        ].map((k,i) => (
          <div key={i} className="kpi" style={{ cursor:"pointer" }} onClick={()=>setSection(sections[i].key)}>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",marginBottom:12,fontWeight:500 }}>{k.label}</div>
            <div style={{ fontFamily:"'Lora',serif",fontSize:28,fontWeight:500,color:k.color }}>{k.val}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginTop:6 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Add Expense button + inline form */}
      <div style={{ marginBottom:20 }}>
        {!showAddExpense ? (
          <button className="btn act" onClick={() => setShowAddExpense(true)} style={{ fontSize:11, padding:"7px 16px" }}>+ Add Manual Expense</button>
        ) : (
          <InboxAddExpenseForm
            pickerJobs={pickerJobs}
            onSubmit={async (entry) => {
              // The form closes only when the expense was saved; App shows the saved or not-saved message.
              const saved = onAddExpense ? await onAddExpense(entry) : false;
              if (saved) setShowAddExpense(false);
            }}
            onCancel={() => setShowAddExpense(false)}
          />
        )}
      </div>

      {/* Section tabs */}
      <div className="r-tabs" style={{ display:"flex",alignItems:"center",gap:0,marginBottom:24,borderBottom:`1px solid ${BORDER}` }}>
        {sections.map(t => (
          <button key={t.key} onClick={()=>setSection(t.key)} style={{
            cursor:"pointer", padding:"10px 20px", fontSize:12, fontWeight:500,
            fontFamily:"'DM Sans',sans-serif", border:"none", borderBottom:`2px solid ${section===t.key?ACCENT:BORDER}`,
            background:"transparent", color:section===t.key?DARK:DIM,
            marginBottom:-1, transition:"all 0.15s",
          }}>
            {t.label}
            {t.count > 0 && (
              <span style={{ marginLeft:7, padding:"1px 7px", borderRadius:10, fontSize:10, fontWeight:600,
                background: section===t.key ? `${t.color}22` : BG2,
                color: section===t.key ? t.color : DIM,
              }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── AUTO-MATCHED section ── */}
      {section === "auto" && (
        <div>
          {filteredAutoMatched.length === 0 ? (
            <div style={{ padding:40,textAlign:"center",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}>No auto-matched expenses yet. Matching improves as you assign more expenses to jobs.</div>
          ) : (
            <div>
              {/* Collapsed summary */}
              <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6,marginBottom:16 }}>
                <div onClick={()=>setExpandAuto(!expandAuto)} style={{ display:"flex",alignItems:"center",gap:12,padding:"16px 20px",cursor:"pointer" }}>
                  <div style={{ width:32,height:32,borderRadius:"50%",background:"rgba(92,122,90,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ color:ACCENT2,fontSize:14 }}>✓</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:500,color:DARK }}>{filteredAutoMatched.length} expense{filteredAutoMatched.length!==1?"s":""} auto-matched</div>
                    <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,marginTop:2 }}>{$(autoTotal)} total · matched by vendor history</div>
                  </div>
                  <span style={{ fontSize:10,color:DIM }}>{expandAuto?"▲ Hide details":"▼ Show details"}</span>
                </div>

                {/* Summary rows by vendor→job */}
                {!expandAuto && autoGrouped.length > 0 && (
                  <div style={{ padding:"0 20px 14px" }}>
                    {autoGrouped.slice(0, 5).map((g, i) => (
                      <div key={i} className="r-wrap" style={{ display:"flex",alignItems:"center",gap:8,padding:"5px 0",fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID }}>
                        <span style={{ fontWeight:500 }}>{g.vendor}</span>
                        <span style={{ color:DIM }}>→</span>
                        <span>{g.jobName}</span>
                        <span style={{ marginLeft:"auto",fontFamily:"'DM Mono',monospace",fontSize:11,color:RED }}>–{$(g.total)}</span>
                        <span style={{ fontSize:10,color:DIM }}>({g.items.length})</span>
                      </div>
                    ))}
                    {autoGrouped.length > 5 && (
                      <div style={{ fontSize:11,color:DIM,fontFamily:"'DM Sans',sans-serif",marginTop:4,cursor:"pointer" }} onClick={()=>setExpandAuto(true)}>+ {autoGrouped.length - 5} more vendor{autoGrouped.length - 5 !== 1 ? "s" : ""}...</div>
                    )}
                  </div>
                )}

                {/* Expanded: individual items with undo */}
                {expandAuto && (
                  <div style={{ borderTop:`1px solid ${BORDER}` }}>
                    {filteredAutoMatched.map((item, idx) => {
                      const job = jobLookup[item.taggedJobId];
                      return (
                        <div key={item.id} className="r-row" style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:idx<filteredAutoMatched.length-1?`1px solid ${BORDER}`:"none" }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
                              <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:500,color:DARK }}>{item.vendor}</span>
                              <span className="mono" style={{ fontSize:10,color:DIM }}>{item.date}</span>
                            </div>
                            <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif" }}>{item.description}</div>
                            <div style={{ fontSize:11,color:ACCENT2,fontFamily:"'DM Sans',sans-serif",marginTop:4 }}>→ {job?.label || 'Unknown'} · {Math.round((item.confidence || 0) * 100)}% confidence</div>
                            {item.matchReason && <div style={{ fontSize:10,color:DIM,fontFamily:"'DM Sans',sans-serif",marginTop:2 }}>{item.matchReason}</div>}
                          </div>
                          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:14,fontWeight:500,color:RED,minWidth:80,textAlign:"right" }}>–{$(item.amount)}</div>
                          <button className="btn" onClick={()=>setConfirmUndo(item)} style={{ fontSize:11,whiteSpace:"nowrap" }}>Undo</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── NEEDS REVIEW section (suggested + needs attention) ── */}
      {section === "review" && (
        <div>
          {needsReviewCount === 0 ? (
            <div style={{ padding:40,textAlign:"center",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}>All caught up — nothing needs review right now.</div>
          ) : (
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>

              {/* Search bar */}
              <div style={{ marginBottom:8 }}>
                <input
                  type="text"
                  placeholder="Search vendor or description..."
                  value={searchReview}
                  onChange={e => setSearchReview(e.target.value)}
                  style={{ width:"100%",maxWidth:400,padding:"8px 14px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,outline:"none" }}
                />
              </div>

              {/* Vendor summary chips — quick glance at which vendors have the most items */}
              {reviewVendorGroups.length > 0 && (
                <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:8 }}>
                  {reviewVendorGroups.slice(0, 6).map(([vendor, g]) => (
                    <button key={vendor} onClick={()=>setSearchReview(vendor)} style={{ cursor:"pointer",padding:"5px 12px",borderRadius:4,border:`1px solid ${BORDER}`,background:searchReview===vendor?ACCENT:CARD,color:searchReview===vendor?CARD:MID,fontFamily:"'DM Sans',sans-serif",fontSize:11,fontWeight:500,transition:"all 0.15s" }}>
                      {vendor} ({g.items.length}) · {$(g.total)}
                    </button>
                  ))}
                  {searchReview && (
                    <button onClick={()=>setSearchReview("")} style={{ cursor:"pointer",padding:"5px 12px",borderRadius:4,border:`1px solid ${BORDER}`,background:"transparent",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:11 }}>Clear ×</button>
                  )}
                </div>
              )}

              {/* Vendor bulk action bar — visible when filtering by vendor */}
              {searchReview && (() => {
                const allVisible = [...filteredSuggested, ...filteredUntagged];
                return allVisible.length >= 2 ? (
                  <div style={{ display:"flex",alignItems:"center",gap:8,padding:"10px 14px",borderRadius:6,background:"rgba(92,122,90,0.06)",border:`1px solid rgba(92,122,90,0.15)`,marginBottom:10,flexWrap:"wrap" }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,color:MID }}>{allVisible.length} items · {$(allVisible.reduce((s,i)=>s+i.amount,0))}</span>
                    <span style={{ color:BORDER }}>|</span>
                    <div className="r-fw" style={{ minWidth:180,flex:"0 1 260px" }}>
                      <JobPicker jobs={pickerJobs} range={expenseRange(allVisible.map(i=>i.date))} value={bulkJobId} onChange={setBulkJobId} placeholder="Tag all to job..." />
                    </div>
                    <button className={`btn${bulkJobId?" act":""}`} disabled={!bulkJobId} onClick={()=>{handleBulkAssign(allVisible,bulkJobId);setBulkJobId("");}} style={{ fontSize:11,opacity:bulkJobId?1:0.45 }}>Tag All →</button>
                    <button className="btn" onClick={()=>handleBulkOverhead(allVisible)} style={{ fontSize:11,borderColor:"rgba(140,107,48,0.3)",color:AMBER }}>All Fixed Costs</button>
                    <button className="btn red" onClick={()=>handleBulkDismiss(allVisible)} style={{ fontSize:11 }}>Dismiss All</button>
                  </div>
                ) : null;
              })()}

              {/* Confirm all suggestions button */}
              {filteredSuggested.length >= 2 && (
                <div style={{ display:"flex",justifyContent:"flex-end",marginBottom:6 }}>
                  <button className="btn act" onClick={handleConfirmAllSuggestions} style={{ fontSize:11,padding:"6px 14px" }}>Confirm All Suggestions ({filteredSuggested.length})</button>
                </div>
              )}

              {/* Suggested matches first */}
              {filteredSuggested.length > 0 && (
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,color:DIM,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12 }}>Suggested Matches · {filteredSuggested.length}</div>
                  {filteredSuggested.map(item => {
                    const job = jobLookup[item.suggestedJob];
                    const hasOverride = !!selections[item.id];
                    return (
                      <div key={item.id} style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6,padding:"16px 20px",marginBottom:8 }}>
                        <div className="r-exp-row" style={{ display:"flex",alignItems:"flex-start",gap:16 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap" }}>
                              <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:500,color:DARK }}>{item.vendor}</span>
                              <span className="mono" style={{ fontSize:10,color:DIM }}>{item.docNumber}</span>
                              <span className="mono" style={{ fontSize:10,color:DIM }}>{item.date}</span>
                              <span className="tag">{item.paymentType}</span>
                            </div>
                            <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif",marginBottom:8 }}>{item.description}</div>
                            <div style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:4,background:"rgba(92,122,90,0.06)",border:`1px solid rgba(92,122,90,0.15)` }}>
                              <span style={{ fontSize:12,color:ACCENT2,fontFamily:"'DM Sans',sans-serif" }}>Suggested: <strong>{job?.label || 'Unknown'}</strong></span>
                              <span style={{ fontSize:10,color:DIM,fontFamily:"'DM Sans',sans-serif" }}>· {Math.round((item.confidence || 0) * 100)}% match</span>
                            </div>
                            {item.matchReason && <div style={{ fontSize:11,color:DIM,fontFamily:"'DM Sans',sans-serif",marginTop:6 }}>{item.matchReason}</div>}
                          </div>
                          <div className="r-exp-side" style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8,minWidth:260 }}>
                            <div className="r-exp-amount" style={{ fontFamily:"'DM Mono',monospace",fontSize:16,fontWeight:500,color:RED }}>–{$(item.amount)}</div>
                            <JobPicker jobs={pickerJobs} range={expenseRange(item.date)} value={selections[item.id]||""} onChange={v=>setSelections(prev=>({...prev,[item.id]:v}))} placeholder="Change job..." clearLabel="Keep the suggested job" style={{ width:"100%" }} />
                            <div className="r-exp-actions" style={{ display:"flex",gap:6 }}>
                              <button className="btn" onClick={()=>{onMarkOverhead(item);showToast(`${item.vendor} marked as fixed cost`,AMBER);}} style={{ borderColor:"rgba(140,107,48,0.3)",color:AMBER }}>Fixed Cost</button>
                              <button className="btn red" onClick={()=>{onDismiss(item.id);showToast(`${item.vendor} dismissed`,DIM);}}>Dismiss</button>
                              {hasOverride ? (
                                <button className="btn act" onClick={()=>handleChangeSuggestion(item)}>Assign →</button>
                              ) : (
                                <button className="btn act" onClick={()=>handleConfirmSuggestion(item)}>Confirm ✓</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Needs attention (no match) */}
              {filteredUntagged.length > 0 && (
                <div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,color:DIM,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12 }}>Needs Attention · {filteredUntagged.length}</div>
                  {filteredUntagged.map(item => (
                    <div key={item.id} style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6,padding:"16px 20px",marginBottom:8 }}>
                      <div className="r-exp-row" style={{ display:"flex",alignItems:"flex-start",gap:16 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap" }}>
                            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:500,color:DARK }}>{item.vendor}</span>
                            <span className="mono" style={{ fontSize:10,color:DIM }}>{item.docNumber}</span>
                            <span className="mono" style={{ fontSize:10,color:DIM }}>{item.date}</span>
                            <span className="tag">{item.paymentType}</span>
                          </div>
                          <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif" }}>{item.description}</div>
                          <div style={{ fontSize:11,color:AMBER,fontFamily:"'DM Sans',sans-serif",marginTop:6 }}>No confident match found</div>
                        </div>
                        <div className="r-exp-side" style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8,minWidth:260 }}>
                          <div className="r-exp-amount" style={{ fontFamily:"'DM Mono',monospace",fontSize:16,fontWeight:500,color:RED }}>–{$(item.amount)}</div>
                          <JobPicker jobs={pickerJobs} range={expenseRange(item.date)} value={selections[item.id]||""} onChange={v=>setSelections(prev=>({...prev,[item.id]:v}))} placeholder="Assign to job..." style={{ width:"100%" }} />
                          <div className="r-exp-actions" style={{ display:"flex",gap:6 }}>
                            <button className="btn red" onClick={()=>{onDismiss(item.id);showToast(`${item.vendor} dismissed`,DIM);}}>Dismiss</button>
                            <button className="btn" onClick={()=>{onMarkOverhead(item);showToast(`${item.vendor} marked as fixed cost`,AMBER);}} style={{ borderColor:"rgba(140,107,48,0.3)",color:AMBER }}>Fixed Cost</button>
                            <button className={`btn${selections[item.id]?" act":""}`} onClick={()=>handleAssign(item)} disabled={!selections[item.id]} style={{ opacity:selections[item.id]?1:0.45 }}>Assign →</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── FIXED COSTS section ── */}
      {section === "fixed" && (
        <div>
          {filteredOverhead.length === 0 ? (
            <div style={{ padding:40,textAlign:"center",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}>No fixed cost expenses yet.</div>
          ) : (
            <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6 }}>
              {filteredOverhead.map((item, idx) => (
                <div key={item.id} className="r-row" style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:idx<filteredOverhead.length-1?`1px solid ${BORDER}`:"none" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4 }}>
                      <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:500,color:DARK }}>{item.vendor}</span>
                      <span className="mono" style={{ fontSize:10,color:DIM }}>{item.date}</span>
                    </div>
                    <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif" }}>{item.description}</div>
                  </div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:500,color:RED }}>–{$(item.amount)}</div>
                  <button className="btn" onClick={()=>onRestore(item.id)} style={{ fontSize:11 }}>Restore</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ALL TAGGED section (with re-tag) ── */}
      {section === "tagged" && (
        <div>
          {/* Search bar */}
          <div style={{ marginBottom:16 }}>
            <input
              type="text"
              placeholder="Search tagged expenses by vendor or description..."
              value={searchTagged}
              onChange={e => setSearchTagged(e.target.value)}
              style={{ width:"100%",maxWidth:400,padding:"8px 14px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,outline:"none" }}
            />
          </div>

          {filteredTagged.length === 0 ? (
            <div style={{ padding:40,textAlign:"center",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}>No tagged expenses yet.</div>
          ) : (
            <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6 }}>
              {filteredTagged.map((item, idx) => {
                const job = jobLookup[item.taggedJobId];
                const isRetaging = retagItem?.id === item.id;
                return (
                  <div key={item.id} style={{ padding:"14px 20px",borderBottom:idx<filteredTagged.length-1?`1px solid ${BORDER}`:"none" }}>
                    <div className="r-row" style={{ display:"flex",alignItems:"center",gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap" }}>
                          <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:500,color:DARK }}>{item.vendor}</span>
                          <span className="mono" style={{ fontSize:10,color:DIM }}>{item.date}</span>
                          <span style={{ fontSize:10,padding:"2px 8px",borderRadius:3,background:`${ACCENT2}15`,color:ACCENT2,fontFamily:"'DM Sans',sans-serif",fontWeight:500 }}>→ {job?.label || item.taggedJobName || 'Unknown'}</span>
                          {item.matchedBy && (
                            <span style={{ fontSize:9,padding:"1px 6px",borderRadius:3,background:BG2,color:DIM,fontFamily:"'DM Sans',sans-serif" }}>{item.matchedBy === 'rule' ? 'auto' : item.matchedBy}</span>
                          )}
                        </div>
                        <div style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif" }}>{item.description}</div>
                      </div>
                      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:500,color:RED,minWidth:80,textAlign:"right" }}>–{$(item.amount)}</div>
                      {!isRetaging && (
                        <button className="btn" onClick={()=>{setRetagItem(item);setRetagJobId("");}} style={{ fontSize:11,whiteSpace:"nowrap" }}>Change job</button>
                      )}
                    </div>

                    {/* Inline re-tag */}
                    {isRetaging && (
                      <div className="r-wrap" style={{ display:"flex",alignItems:"center",gap:8,marginTop:10,padding:"10px 14px",borderRadius:5,background:BG2,border:`1px solid ${BORDER}` }}>
                        <span style={{ fontSize:12,color:MID,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap" }}>Move to:</span>
                        <div style={{ flex:1,minWidth:0 }}>
                          <JobPicker jobs={pickerJobs} range={expenseRange(item.date)} value={retagJobId} onChange={setRetagJobId} placeholder="Select new job..." />
                        </div>
                        <button className="btn" onClick={()=>{setRetagItem(null);setRetagJobId("");}}>Cancel</button>
                        <button className={`btn${retagJobId?" act":""}`} onClick={handleRetagSubmit} disabled={!retagJobId} style={{ opacity:retagJobId?1:0.45 }}>Save →</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAGGED IN QB section ── */}
      {section === "qbtagged" && (
        <div>
          {/* Info callout */}
          <div style={{ marginBottom:16,padding:"12px 16px",borderRadius:6,background:"rgba(92,122,90,0.08)",border:`1px solid rgba(92,122,90,0.2)`,display:"flex",gap:10,alignItems:"flex-start" }}>
            <span style={{ fontFamily:"'DM Mono',monospace",fontSize:13,color:ACCENT2,flexShrink:0,fontWeight:600 }}>i</span>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,lineHeight:1.6 }}>
              You already assigned these expenses to jobs in QuickBooks. They count toward job costs, and you don't need to do anything here.
            </div>
          </div>

          {/* Search */}
          <div style={{ marginBottom:16 }}>
            <input
              type="text"
              placeholder="Search by vendor, description, or job..."
              value={searchQb}
              onChange={e => setSearchQb(e.target.value)}
              style={{ width:"100%",maxWidth:400,padding:"8px 14px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,outline:"none" }}
            />
          </div>

          {/* Summary bar */}
          {qbDirect.length > 0 && (
            <div style={{ marginBottom:14,display:"flex",alignItems:"center",gap:16,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>
              <span><strong style={{ color:DARK }}>{qbDirect.length}</strong> expense{qbDirect.length!==1?"s":""}</span>
              <span style={{ fontFamily:"'DM Mono',monospace",color:RED,fontWeight:500 }}>–{$(qbDirectTotal)}</span>
              <span>already in job costs</span>
            </div>
          )}

          {qbDirect.length === 0 ? (
            <div style={{ padding:40,textAlign:"center",color:DIM,fontFamily:"'DM Sans',sans-serif",fontSize:13 }}>No expenses were assigned to jobs in QuickBooks in this date range.</div>
          ) : (
            <div className="r-scroll" style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:6 }}>
              <div style={{ minWidth:620 }}>
              {/* Column headers */}
              <div style={{ display:"grid",gridTemplateColumns:"1.2fr 1.2fr 2fr 110px 110px",gap:8,padding:"8px 20px",borderBottom:`1px solid ${BORDER}`,fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.1em",textTransform:"uppercase",color:DIM,fontWeight:500 }}>
                <span>Vendor</span><span>Job</span><span>Description</span><span>Date</span><span style={{ textAlign:"right" }}>Amount</span>
              </div>
              {qbDirect.map((p, idx) => (
                <div key={p.Id} style={{ display:"grid",gridTemplateColumns:"1.2fr 1.2fr 2fr 110px 110px",gap:8,padding:"10px 20px",borderBottom:idx<qbDirect.length-1?`1px solid ${BORDER}`:"none",alignItems:"center" }}>
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,color:DARK,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.EntityRef?.name||'Unknown'}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:ACCENT2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.jobName}</div>
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:MID,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.Line?.[0]?.Description||'—'}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:11,color:DIM }}>{p.TxnDate}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace",fontSize:12,color:RED,fontWeight:500,textAlign:"right" }}>–{$(p.TotalAmt||0)}</div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export { SyncReview };
